import { matchPath } from 'react-router-dom'

import type { PairRouteMode } from '../api/pairs'
import { isNonTonQuote } from './tonQuote'
import { DEFAULT_TRADE_PRESETS } from './tradePanel'

export function primaryPairPath(master: string): string {
  return `/${encodeURIComponent(master)}`
}

export interface PairSeedRef {
  addr: string
  mode?: PairRouteMode
}

export interface PairRouteNavigationState {
  pairPoolHint: string
}

export function pairRouteNavigationState(
  poolAddr?: string | null
): PairRouteNavigationState | undefined {
  const pairPoolHint = poolAddr?.trim()
  return pairPoolHint ? { pairPoolHint } : undefined
}

/** Discover/Trenches Buy: land on the pair and fire the same Rail toast as a
 *  regular trade. Instant must not open. `preset` is the trading preset the
 *  list's P1–P3 chose: the index of one of the trade form's presets, 0 for
 *  the first. */
export type ListBuyIntent = { master: string; gram: string; preset: number }

/** MONEY PATH. How long a list buy may wait, from the press, for the page of
 *  its pool to open and its form to send it. Past this the buy is dropped:
 *  nothing is bought and its toast says so — a buy must not go out long after
 *  it was pressed (it used to wait without end, and went out whenever a page
 *  of the token next opened).
 *
 *  The figure is measured, not chosen by feel. From the press to the form's
 *  quote, on a token whose page is opened twice (a migrated launchpad token:
 *  the DEX route, then the canonical one — the slowest landing there is):
 *  2.6 s as it loads, 4.7–5.2 s with the pair seed held back another 2.5 s
 *  (r5-fixes/rereview/B-timeline-*.log, 2026-10-13, through the dev proxy).
 *  10 s is about four times the first and twice the second. */
export const LIST_BUY_DEADLINE_MS = 10_000

// one of the trade form's presets, by its index
const isTradePreset = (value: unknown): value is number =>
  typeof value === 'number' &&
  Number.isInteger(value) &&
  value >= 0 &&
  value < DEFAULT_TRADE_PRESETS.length

/** `wallet`: the trading wallet the list had at the press (the account's
 *  primary, which every form signs from) — the buy goes out from no other
 *  (PairPage drops it when the trading wallet is another). None when the list
 *  had none. */
export function listBuyNavigationState(
  poolAddr: string | null | undefined,
  amount: string,
  preset = 0,
  pressedAt: number = Date.now(),
  wallet?: string | null
): {
  autoBuy: true
  autoBuyAt: number
  autoBuyGram?: string
  autoBuyPreset?: number
  autoBuyWallet?: string
  pairPoolHint?: string
} {
  const gram = amount.trim()
  const pressedWith = wallet?.trim()
  return {
    ...(pairRouteNavigationState(poolAddr) ?? {}),
    autoBuy: true,
    // MONEY PATH: the moment of this buy's press. Every list builds its buy
    // here; the buy's deadline is measured from this stamp. Now, when a list
    // builds it in its click; the press time it kept, when its press waited
    // (Search, for its buy's function to load).
    autoBuyAt: pressedAt,
    ...(gram ? { autoBuyGram: gram } : {}),
    // the first preset is the form's own default: only another one is carried
    ...(isTradePreset(preset) && preset !== 0 ? { autoBuyPreset: preset } : {}),
    // MONEY PATH: the wallet the buy was pressed with
    ...(pressedWith ? { autoBuyWallet: pressedWith } : {}),
  }
}

/** MONEY PATH. The trading wallet a list buy was pressed with, as its list
 *  stamped it; null when it carries none. */
export function listBuyWalletFromNavigationState(state: unknown): string | null {
  if (state == null || typeof state !== 'object') return null
  const wallet = (state as { autoBuyWallet?: unknown }).autoBuyWallet
  return typeof wallet === 'string' && wallet.trim() ? wallet.trim() : null
}

export function listBuyFromNavigationState(
  state: unknown,
  master: string | undefined
): ListBuyIntent | null {
  if (!master || state == null || typeof state !== 'object') return null
  const rec = state as { autoBuy?: unknown; autoBuyGram?: unknown; autoBuyPreset?: unknown }
  if (rec.autoBuy !== true) return null
  const gram = typeof rec.autoBuyGram === 'string' ? rec.autoBuyGram.trim() : ''
  // anything that is not one of the form's presets is the first one
  const preset = isTradePreset(rec.autoBuyPreset) ? rec.autoBuyPreset : 0
  return { master, gram, preset }
}

/** MONEY PATH. How long the list buy a location carries may still wait, at
 *  `now`: its deadline less the time since its PRESS — not since a page took
 *  it (a history entry can carry a buy for minutes before any page mounts for
 *  it). Nothing is left for a buy with no press time: an unknown age is never
 *  trusted. Never more than the deadline: a clock that moved cannot lengthen
 *  the wait. */
export function listBuyRemainingMs(state: unknown, now: number): number {
  const pressedAt =
    state != null && typeof state === 'object' ? (state as { autoBuyAt?: unknown }).autoBuyAt : null
  if (typeof pressedAt !== 'number' || !Number.isFinite(pressedAt)) return 0
  return Math.min(LIST_BUY_DEADLINE_MS, Math.max(0, LIST_BUY_DEADLINE_MS - (now - pressedAt)))
}

export function withoutListBuyNavigationState(state: unknown): unknown {
  if (state == null || typeof state !== 'object') return state
  return Object.fromEntries(
    Object.entries(state as Record<string, unknown>).filter(
      ([key]) =>
        key !== 'autoBuy' &&
        key !== 'autoBuyAt' &&
        key !== 'autoBuyGram' &&
        key !== 'autoBuyPreset' &&
        key !== 'autoBuyWallet'
    )
  )
}

export function pairPoolHintFromNavigationState(state: unknown): string | undefined {
  if (state == null || typeof state !== 'object') return undefined
  const value = (state as { pairPoolHint?: unknown }).pairPoolHint
  return typeof value === 'string' && value.trim() ? value.trim() : undefined
}

export function launchpadPairPath(master: string, source: string): string {
  return `/${encodeURIComponent(master)}/${encodeURIComponent(source.trim().toLowerCase())}`
}

export function dexPoolPairPath(dex: string, poolAddr: string): string {
  return `/${encodeURIComponent(dex.trim().toLowerCase())}/${encodeURIComponent(poolAddr)}`
}

export function dexPairPath(master: string, dex: string): string {
  return `/${encodeURIComponent(master)}/${encodeURIComponent(dex.trim().toLowerCase())}`
}

// The (addr, mode) usePairFeed will request for this row's navigation target.
// searchPairPath is derived from this exact ref, so a hover prefetch keyed on
// it can never drift from the route PairPage parses back.
export function searchPairSeedRef(r: {
  master: string
  pool?: string
  dex?: string
  quoteToken?: string | null
  launchpadSource?: string | null
  launchpadStatus?: string | null
}): PairSeedRef {
  // A still-bonding launchpad row is the curve, not a DEX pool → phase page.
  if (r.launchpadSource && r.launchpadStatus === 'bonding') {
    return {
      addr: r.master,
      mode: { phase: 'launchpad', source: r.launchpadSource.trim().toLowerCase() },
    }
  }
  // A pool without TON opens as itself, the way its Discover row does. The
  // requested-dex route is resolved among TON-quoted pools only, so it would
  // find nothing for this row, or another pool of the same token.
  if (r.pool && r.pool !== r.master && isNonTonQuote(r.quoteToken)) {
    return { addr: r.pool }
  }
  // A DEX pool row opens the canonical requested-dex route.
  if (r.dex && r.pool && r.pool !== r.master) {
    return { addr: r.master, mode: { dex: r.dex.trim().toLowerCase() } }
  }
  return { addr: r.master }
}

export function searchPairPath(r: {
  master: string
  pool?: string
  dex?: string
  quoteToken?: string | null
  launchpadSource?: string | null
  launchpadStatus?: string | null
}): string {
  const ref = searchPairSeedRef(r)
  if (ref.mode?.phase === 'launchpad' && ref.mode.source) {
    return launchpadPairPath(ref.addr, ref.mode.source)
  }
  // Legacy concrete pool routes still resolve, but PairPage redirects them
  // after loading detail.
  if (ref.mode?.dex) {
    return dexPairPath(ref.addr, ref.mode.dex)
  }
  return primaryPairPath(ref.addr)
}

// ---- Direct-URL route parsing (module-init boot path) ----
//
// The same pair-route parsing PairPage applies to useParams, reachable from
// main.tsx BEFORE React boots so a direct token-page load can start the
// read-only /resolve request and the PairPage chunk during bundle evaluation.
// PairPage re-exports pairRouteTarget from here; do not fork the logic.

export const DEX_ROUTE_PREFIXES = new Set([
  'dedust',
  'dedust_v1',
  'dedust_v2',
  'stonfi',
  'stonfi_v1',
  'stonfi_v2',
])
export const LAUNCHPAD_ROUTE_SUFFIXES = new Set([
  'uranus',
  'blum',
  'tonfun',
  'gaspump',
  'stonks',
  'wils',
])

export interface PairRouteParams extends Record<string, string | undefined> {
  addr?: string
  left?: string
  right?: string
  pool?: string
}

export interface PairRouteTarget extends PairRouteMode {
  addr?: string
}

export function pairRouteTarget(params: PairRouteParams): PairRouteTarget {
  if (params.addr) return { addr: params.addr }
  if (!params.left) return {}
  if (!params.right) return { addr: params.left }
  const left = params.left.toLowerCase()
  const right = params.right.toLowerCase()
  if (LAUNCHPAD_ROUTE_SUFFIXES.has(left)) {
    return { addr: params.right, phase: 'launchpad', source: left }
  }
  if (params.pool && DEX_ROUTE_PREFIXES.has(right)) return { addr: params.pool, dex: right }
  if (DEX_ROUTE_PREFIXES.has(left)) return { addr: params.right }
  if (DEX_ROUTE_PREFIXES.has(right)) return { addr: params.left, dex: right }
  if (LAUNCHPAD_ROUTE_SUFFIXES.has(right)) {
    return { addr: params.left, phase: 'launchpad', source: right }
  }
  return { addr: params.left }
}

export interface PairRouteRef {
  addr: string
  mode?: PairRouteMode
}

// Top-level segments that are their own routes, never pair refs. Mirrors the
// AppRouter table: a bare '/discover' or '/portfolio/<addr>' must not spawn a
// speculative pair /resolve.
const NON_PAIR_TOP_LEVEL = new Set([
  'discover',
  'pulse',
  'portfolio',
  'rewards',
  'tokens',
  'test',
  'design-lab',
])

const PAIR_ROUTE_PATTERNS = ['/:left/:right/:pool', '/:left/:right', '/:addr'] as const

/** Parses a raw pathname into the (addr, mode) usePairFeed will request, or
    null when the URL is not a pair route. Exact-match only; unknown shapes
    (e.g. /test/chart) return null. */
export function pairRouteRefFromPathname(pathname: string): PairRouteRef | null {
  const pairMatch = matchPath({ path: '/pair/:addr', end: true }, pathname)
  if (pairMatch?.params.addr) return { addr: pairMatch.params.addr }
  const first = pathname.split('/')[1]?.toLowerCase() ?? ''
  if (NON_PAIR_TOP_LEVEL.has(first)) return null
  for (const pattern of PAIR_ROUTE_PATTERNS) {
    const match = matchPath({ path: pattern, end: true }, pathname)
    if (!match) continue
    const target = pairRouteTarget({
      addr: match.params.addr,
      left: match.params.left,
      right: match.params.right,
      pool: match.params.pool,
    })
    if (!target.addr) continue
    const mode =
      target.phase || target.dex
        ? { phase: target.phase, source: target.source, dex: target.dex }
        : undefined
    return { addr: target.addr, mode }
  }
  return null
}
